import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Download,
  Plus,
  AlertTriangle,
  Users,
  RefreshCw,
  MoreVertical,
  Eye,
  Pencil,
  Printer,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import type { Patient } from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { avatarGradient } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePageTitle } from '@/shared/store/page';
import { ageFromBirthdate, initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { usePatientsList } from './api';

const DEFAULT_PAGE_SIZE = 20;

/** Debounce ligero para el campo de búsqueda — evita ráfagas de queries. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

function isAllergyMeaningful(alergias: string | undefined): boolean {
  if (alergias === undefined) return false;
  const trimmed = alergias.trim().toLowerCase();
  return trimmed !== '' && trimmed !== 'ninguna' && trimmed !== 'ninguna reportada';
}

export function PatientsListPage(): JSX.Element {
  usePageTitle('Pacientes', 'Registro único por persona — nunca se duplica');
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const [allergyOnly, setAllergyOnly] = useState<boolean>(false);
  // Orden por número de procedimientos: '' (por defecto) → desc → asc → ''.
  const [sort, setSort] = useState<'' | 'procedimientosCount:desc' | 'procedimientosCount:asc'>('');
  const [page, setPage] = useState<number>(1);

  // Cuando cambia el término de búsqueda, volver a la primera página.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const cycleSortProc = (): void => {
    setSort((s) =>
      s === 'procedimientosCount:desc'
        ? 'procedimientosCount:asc'
        : s === 'procedimientosCount:asc'
          ? ''
          : 'procedimientosCount:desc',
    );
    setPage(1);
  };

  const { data, isLoading, isFetching, isError, error, refetch } = usePatientsList({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    q: debouncedQuery,
    ...(sort !== '' ? { sort } : {}),
  });

  const visiblePatients = useMemo<Patient[]>(() => {
    if (data === undefined) return [];
    if (!allergyOnly) return data.items;
    return data.items.filter((p) => isAllergyMeaningful(p.alergias));
  }, [data, allergyOnly]);

  const total = data?.total ?? 0;
  const totalPages = total === 0 ? 1 : Math.ceil(total / DEFAULT_PAGE_SIZE);

  return (
    <div className="flex flex-col gap-5">
      {/* Toolbar */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="relative min-w-[320px] flex-1 max-w-[480px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            placeholder="Buscar por nombre, documento, historia clínica o teléfono…"
            className="pl-10"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Button
          variant={allergyOnly ? 'primary' : 'secondary'}
          onClick={() => setAllergyOnly((v) => !v)}
        >
          <AlertTriangle className="w-4 h-4" /> Solo con alergias
        </Button>
        <Button variant="secondary" disabled>
          <Filter className="w-4 h-4" /> Más filtros
        </Button>
        <div className="flex-1" />
        <Button variant="secondary" disabled>
          <Download className="w-4 h-4" /> Exportar
        </Button>
        <Button onClick={() => navigate('/pacientes/nuevo')}>
          <Plus className="w-4 h-4" /> Nuevo paciente
        </Button>
      </div>

      {/* Chips de filtros activos */}
      {(debouncedQuery !== '' || allergyOnly) && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-text-muted">Filtros activos:</span>
          {debouncedQuery !== '' && (
            <FilterChip label={`Búsqueda: "${debouncedQuery}"`} onClear={() => setQuery('')} />
          )}
          {allergyOnly && (
            <FilterChip label="Solo con alergias" onClear={() => setAllergyOnly(false)} />
          )}
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setAllergyOnly(false);
            }}
            className="text-text-muted hover:text-text underline"
          >
            Limpiar todo
          </button>
        </div>
      )}

      {isError && (
        <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar el listado</div>
            <div className="text-sm text-red-700">{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Listado de pacientes</CardTitle>
            <CardDescription>
              {isLoading
                ? 'Cargando…'
                : `${visiblePatients.length} de ${total} ${total === 1 ? 'registro' : 'registros'}`}
              {isFetching && !isLoading ? ' · actualizando…' : ''}
            </CardDescription>
          </div>
        </CardHeader>

        {isLoading ? (
          <ListSkeleton />
        ) : !isError && visiblePatients.length === 0 ? (
          <EmptyState
            onCreate={() => navigate('/pacientes/nuevo')}
            hasFilters={debouncedQuery !== '' || allergyOnly}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Paciente</TableHead>
                <TableHead>Documento / Contacto</TableHead>
                <TableHead>Edad / Sexo</TableHead>
                <TableHead>EPS</TableHead>
                <TableHead>Alergias</TableHead>
                <TableHead className="text-right">
                  <button
                    type="button"
                    onClick={cycleSortProc}
                    title="Ordenar por número de procedimientos"
                    className="inline-flex items-center gap-1 uppercase tracking-wider font-bold text-text-muted hover:text-text"
                  >
                    # Proc.
                    {sort === 'procedimientosCount:desc' ? (
                      <ChevronDown className="w-3.5 h-3.5 text-primary" />
                    ) : sort === 'procedimientosCount:asc' ? (
                      <ChevronUp className="w-3.5 h-3.5 text-primary" />
                    ) : (
                      <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="w-[52px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="stagger-children">
              {visiblePatients.map((p) => {
                const edad = ageFromBirthdate(p.fechaNacimiento);
                return (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer animate-fade-in-up hover:bg-surface-alt transition-colors"
                    onClick={() => navigate(`/pacientes/${p.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div
                          className={cn(
                            'w-9 h-9 rounded-md grid place-items-center font-bold text-xs text-white flex-shrink-0',
                            avatarGradient(p.id),
                          )}
                        >
                          {initials(p.nombres, p.apellidos)}
                        </div>
                        <div>
                          <div className="font-semibold text-text">
                            {p.nombres} {p.apellidos}
                          </div>
                          <div className="text-text-muted text-xs">
                            {p.historiaClinica ?? p.id}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold">
                        {p.tipoDoc} {p.documento}
                      </div>
                      <div className="text-text-muted text-xs">{p.telefono ?? '—'}</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">{edad}</span>{' '}
                      <span className="text-text-muted text-xs">
                        años ·{' '}
                        {p.sexo === 'F'
                          ? 'Femenino'
                          : p.sexo === 'M'
                            ? 'Masculino'
                            : 'Otro'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {p.eps !== undefined && p.eps !== '' ? (
                        <Badge variant="blue">{p.eps}</Badge>
                      ) : (
                        <span className="text-text-muted text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isAllergyMeaningful(p.alergias) ? (
                        <Badge variant="amber">{p.alergias}</Badge>
                      ) : (
                        <Badge variant="gray">Ninguna</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-bold">{p.procedimientosCount}</span>
                    </TableCell>
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-default pl-1 pr-3 text-right"
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={`Acciones para ${p.nombres}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => navigate(`/pacientes/${p.id}`)}>
                            <Eye className="w-4 h-4" /> Ver detalle
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => navigate(`/pacientes/${p.id}/editar`)}
                          >
                            <Pencil className="w-4 h-4" /> Editar paciente
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => navigate(`/pacientes/${p.id}/imprimir`)}
                          >
                            <Printer className="w-4 h-4" /> Imprimir historia
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!isLoading && !isError && total > 0 && (
          <CardFooter className="justify-between">
            <div className="text-text-muted text-sm">
              Página {page} de {totalPages} · {total} {total === 1 ? 'paciente' : 'pacientes'} en
              total
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="p-6 flex flex-col gap-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-12 rounded-md bg-surface-alt animate-pulse"
          style={{ opacity: 1 - i * 0.1 }}
        />
      ))}
    </div>
  );
}

interface EmptyStateProps {
  onCreate(): void;
  hasFilters: boolean;
}

function EmptyState({ onCreate, hasFilters }: EmptyStateProps): JSX.Element {
  return (
    <div className="p-10 text-center flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-surface-alt grid place-items-center text-text-muted">
        <Users className="w-6 h-6" />
      </div>
      <div className="text-text font-semibold">
        {hasFilters ? 'Sin coincidencias' : 'Aún no hay pacientes'}
      </div>
      <p className="text-text-muted text-sm max-w-sm">
        {hasFilters
          ? 'Ajusta los filtros o el término de búsqueda para ver resultados.'
          : 'Crea el primer paciente para empezar a registrar procedimientos.'}
      </p>
      {!hasFilters && (
        <Button onClick={onCreate}>
          <Plus className="w-4 h-4" /> Nuevo paciente
        </Button>
      )}
    </div>
  );
}

/** Chip removible para filtros activos. */
function FilterChip({ label, onClear }: { label: string; onClear: () => void }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-soft text-primary border border-primary/20 text-[11px] font-semibold">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Quitar ${label}`}
        className="rounded-full p-0.5 hover:bg-primary/15"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
